import { useCallback, useEffect, useMemo, useState } from "react";
import { api } from "../lib/api";
import { useParams, useSearchParams } from "react-router-dom";
import { KEY_STATUSES, STAGE_COLORS, STAGE_LABELS } from "../lib/stages";
import { FlagPip } from "../components/StageBadge";
import RoomDrawer, { InspectionFormDialog, StageFormDialog } from "../components/RoomDrawer";
import { AlertTriangle, Star, Clock, Bed, Home } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { canAccessStage } from "../lib/permissions";
import { toast } from "sonner";
import { RepairForm } from "./Repairs";
import MobileFilterPanel from "../components/MobileFilterPanel";
import { useApplication } from "../context/ApplicationContext";
import { PageHeader, EmptyState } from "../components/OperationalUI";
import { AssignmentBadge, AssignmentPicker } from "../components/Assignment";
import { friendlyStatus } from "../lib/presentation";
import ResponsiveDialog from "../components/ResponsiveDialog";
import { repairLocation } from "../lib/location";
import { ASSIGNMENT_FILTER_OPTIONS, assignmentMatches, stageMetadata, stageModalKind } from "../lib/stageExperience";

const TAB_DESC = {
  key_return: "Rooms awaiting key return — set a key status to start the workflow.",
  checkout_inspection: "Submit checkout inspection (storage / damages / bag & bin).",
  bag_and_bin: "Waste removal pending.",
  maintenance_inspection: "Record painting and repair lists.",
  internal_repairs: "Open internal repairs.",
  external_repairs: "Open external repairs.",
  painting: "Painting work outstanding.",
  cleaning: "Cleaning checklist + mattress protector size.",
  cleaning_inspection: "Pass or fail the cleaning.",
  welcome_packs: "Welcome pack delivery (bedrooms only).",
  completed: "Fully turned around.",
};

const STAGE_TAB = {
  checkout_inspection: "inspections",
  maintenance_inspection: "inspections",
  internal_repairs: "repairs",
  external_repairs: "repairs",
};

const TYPE_OPTIONS = [
  { id: "all", label: "All" },
  { id: "bedroom", label: <span className="inline-flex items-center gap-1"><Bed size={11}/>Bedrooms</span> },
  { id: "common", label: <span className="inline-flex items-center gap-1"><Home size={11}/>Common</span> },
];

const TRI_OPTIONS = [
  { id: "all", label: "All" },
  { id: "only", label: "Only" },
  { id: "exclude", label: "Hide" },
];

const BI_OPTIONS = [
  { id: "all", label: "All" },
  { id: "only", label: "Only" },
];

const VIEW_OPTIONS = [
  { id: "active", label: "Pending" },
  { id: "completed", label: "Completed" },
];

const BAG_AND_BIN_VIEW_OPTIONS = [
  ...VIEW_OPTIONS,
  { id: "pending_delegated", label: "Pending Delegated" },
  { id: "completed_by_cleaners", label: "Completed by cleaners" },
];

const REPAIR_STAGES = new Set(["internal_repairs", "external_repairs"]);

function flagsForRoom(r) {
  return [
    r.flags?.rebooking && "Rebooking",
    r.flags?.storage && "Storage",
    r.flags?.summer_stay && "Summer Stay",
    r.flags?.refurbishment && "Refurbishment",
    r.priority && "Priority",
  ].filter(Boolean).join(" | ");
}

function statusForStage(r, stage) {
  return stage === "completed" ? "completed" : r.stages?.[stage]?.status || "pending";
}

function noteForStage(r, stage) {
  return stage === "completed" ? "" : r.stages?.[stage]?.note || "";
}

function completedAtForStage(r, stage) {
  return r.stage_completed_at?.[stage] || r.stages?.[stage]?.completed_at || "";
}

function completedByForStage(r, stage) {
  return r.stage_completed_by?.[stage] || "";
}

function repairRowsForRoom(r, stage, repairs, viewFilter) {
  if (!REPAIR_STAGES.has(stage)) return [];
  const wantedKind = stage === "internal_repairs" ? "internal" : "external";
  return (repairs || [])
    .filter((repair) => repair.room_id === r.id && repair.kind === wantedKind)
    .filter((repair) => (viewFilter === "completed" || viewFilter === "completed_by_cleaners") ? repair.status === "completed" : repair.status !== "completed");
}


function buildStageExportRows(stage, rooms, repairs, viewFilter) {
  const label = STAGE_LABELS[stage] || stage;
  const completionNotesFor = (repair) => repair.completion_notes || (repair.status === "completed" ? (repair.comments || []).at(-1)?.text : "") || "";
  if (REPAIR_STAGES.has(stage)) {
    return [
      ["Site", "Block", "Flat", "Room", "Repair Type", "Repair Description", "Repair Status", "Hold Reason", "Contractor Ref", "Completion Notes", "Stage Status", "Stage Note", ...((viewFilter === "completed" || viewFilter === "completed_by_cleaners") ? ["Stage Completed At", "Completed By"] : []), "Flags"],
      ...rooms.flatMap((r) => {
        const repairRows = repairRowsForRoom(r, stage, repairs, viewFilter);
        if (repairRows.length === 0) {
          return [[
            r.site_name || "Unknown site",
            r.block_name || "",
            r.flat_name || "",
            r.name || "",
            stage === "internal_repairs" ? "Internal" : "External",
            "No matching repairs listed",
            "",
            "",
            "",
            "",
            statusForStage(r, stage),
            noteForStage(r, stage),
            ...((viewFilter === "completed" || viewFilter === "completed_by_cleaners") ? [completedAtForStage(r, stage), completedByForStage(r, stage)] : []),
            flagsForRoom(r),
          ]];
        }
        return repairRows.map((repair) => [
          r.site_name || "Unknown site",
          r.block_name || repair.block_name || "",
          r.flat_name || repair.flat_name || "",
          r.name || repair.room_name || "",
          repair.kind || "",
          repair.description || "",
          String(repair.status || "").replace(/_/g, " "),
          repair.hold_reason || "",
          repair.job_number || "",
          completionNotesFor(repair),
          statusForStage(r, stage),
          noteForStage(r, stage),
          ...((viewFilter === "completed" || viewFilter === "completed_by_cleaners") ? [completedAtForStage(r, stage), completedByForStage(r, stage)] : []),
          flagsForRoom(r),
        ]);
      }),
    ];
  }

  const extraHeaders = {
    key_return: ["Contract End", "Overstayer"],
    checkout_inspection: ["Storage", "Bag & Bin Required", "Damages / Charges", "Notes"],
    bag_and_bin: ["Delegated To Cleaner", "Checkout Bag & Bin Photo"],
    maintenance_inspection: ["Painting Required", "Painting Comments", "Internal Repairs", "External Repairs", "Notes"],
    painting: [],
    cleaning: [
      "Bathroom",
      "Bed Size",
      "Returned From Inspection Reason",
      ...((viewFilter === "completed" || viewFilter === "completed_by_cleaners") ? ["Shower Curtain", "Hoover Bag", "Extractor Filter", "Bag & Bin Completed", "Mattress Protector Placed"] : []),
      "Cleaning Repairs Added",
    ],
    cleaning_inspection: ["Result", "Fail Reason", "Notes"],
    welcome_packs: ["Delivered", "Delivered On", "Notes"],
    refurbishment: ["Refurb Note", "Returned From Inspection Reason"],
    refurbishment_inspection: ["Result", "Fail Reason", "Notes"],
  }[stage] || [];

  const extraValues = (r) => {
    if (stage === "key_return") return [r.contract_end_date || "", r.overstayer ? "Yes" : "No"];
    if (stage === "checkout_inspection") {
      const co = r.checkout_inspection || r.stages?.checkout_inspection?.form_data || {};
      const damages = (co.damage_items || []).map((d) => `${d.description || "Damage"}${d.amount ? ` (£${d.amount})` : ""}`).join(" | ");
      return [co.storage ? "Yes" : "No", co.bag_and_bin_required ? "Yes" : "No", damages, co.notes || ""];
    }
    if (stage === "bag_and_bin") {
      const co = r.checkout_inspection || r.stages?.checkout_inspection?.form_data || {};
      return [r.bag_and_bin_delegated_to_cleaner ? "Yes" : "No", co.bag_and_bin_photo_path || ""];
    }
    if (stage === "maintenance_inspection") {
      const mi = r.maintenance_inspection || r.stages?.maintenance_inspection?.form_data || {};
      return [
        mi.painting_required ? "Yes" : "No",
        mi.painting_comments || "",
        (mi.internal_repairs || []).join(" | "),
        (mi.external_repairs || []).join(" | "),
        mi.notes || "",
      ];
    }
    if (stage === "painting") return [];
    if (stage === "cleaning") {
      const ci = r.stages?.cleaning_inspection?.form_data || {};
      const cleaning = r.stages?.cleaning?.form_data || {};
      const addedRepairs = (cleaning.repairs_to_add || []).map((repair) => repair.description || repair).filter(Boolean).join(" | ");
      const base = [r.type === "common" ? "Common area" : r.bathroom === "ensuite" ? "Ensuite" : "Shared", r.bed_size || "", ci.fail_reason || ""];
      if ((viewFilter === "completed" || viewFilter === "completed_by_cleaners")) {
        base.push(
          formatCleaningAnswer(cleaning.shower_curtain),
          formatCleaningAnswer(cleaning.hoover_bag),
          formatCleaningAnswer(cleaning.extractor_filter),
          formatCleaningAnswer(cleaning.bag_bin_done),
          formatCleaningAnswer(cleaning.mattress_protector)
        );
      }
      return [...base, addedRepairs];
    }
    if (stage === "cleaning_inspection" || stage === "refurbishment_inspection") {
      const data = r.stages?.[stage]?.form_data || {};
      return [data.result || "", data.fail_reason || "", data.notes || data.note || ""];
    }
    if (stage === "welcome_packs") {
      const data = r.stages?.welcome_packs?.form_data || {};
      return [data.delivered ? "Yes" : "No", data.delivered_on || "", data.note || ""];
    }
    if (stage === "refurbishment") {
      const ri = r.stages?.refurbishment_inspection?.form_data || {};
      return [r.stages?.refurbishment?.note || "", ri.fail_reason || ""];
    }
    return [];
  };

  return [
    ["Site", "Block", "Flat", "Room", "Type", "Stage", "Stage Status", "Stage Note", ...((viewFilter === "completed" || viewFilter === "completed_by_cleaners") ? ["Stage Completed At", "Completed By"] : []), "Flags", ...extraHeaders],
    ...rooms.map((r) => [
      r.site_name || "Unknown site",
      r.block_name || "",
      r.flat_name || "",
      r.name || "",
      r.type || "",
      label,
      statusForStage(r, stage),
      noteForStage(r, stage),
      ...((viewFilter === "completed" || viewFilter === "completed_by_cleaners") ? [completedAtForStage(r, stage), completedByForStage(r, stage)] : []),
      flagsForRoom(r),
      ...extraValues(r),
    ]),
  ];
}

function formatCleaningAnswer(value) {
  if (value === "yes") return "Yes";
  if (value === "no") return "No";
  if (value === "na") return "N/A";
  if (typeof value === "boolean") return value ? "Yes" : "No";
  return "";
}

function csvEscape(value) {
  const text = String(value ?? "");
  if (/[",\n]/.test(text)) return `"${text.replace(/"/g, '""')}"`;
  return text;
}

function downloadCsv(filename, rows) {
  const csv = rows.map((row) => row.map(csvEscape).join(",")).join("\n");
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

export default function StageList() {
  const { effectiveWorkflow } = useApplication();
  const { stage } = useParams();
  const { user } = useAuth();
  const canReturnBagBin = (user?.permission_keys||[]).includes("tar.bag_bin.return_internal");
  const [params, setParams] = useSearchParams();
  const [rooms, setRooms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [blocks, setBlocks] = useState([]);
  const [settings, setSettings] = useState({ highlight_priority: "#FEF08A", highlight_stale: "#FED7AA" });
  const [cleaningExportFormat, setCleaningExportFormat] = useState("csv");
  const [bulkMode, setBulkMode] = useState(false);
  const [picked, setPicked] = useState(() => new Set());
  const roomFromNotif = params.get("room");
  const [bulkBusy, setBulkBusy] = useState(false);
  const [bulkKeyStatus, setBulkKeyStatus] = useState(KEY_STATUSES[0]);
  const [bulkHoldReason, setBulkHoldReason] = useState("");
  const [selectedRepair, setSelectedRepair] = useState(null);
  const [selectedStageForm, setSelectedStageForm] = useState(null);
  const [stageRepairs, setStageRepairs] = useState([]);
  const [configuredBulkActions,setConfiguredBulkActions]=useState([]);
  const [bulkResult,setBulkResult]=useState(null);
  const [bulkAssignOpen,setBulkAssignOpen]=useState(false);
  const [keyReturnRoom,setKeyReturnRoom]=useState(null);
  const [assignmentOptions,setAssignmentOptions]=useState({users:[],roles:[]});
  const [savedFilters,setSavedFilters]=useState(null);

  const typeFilter = params.get("type") || "all"; // all | bedroom | common
  const blockFilter = params.get("block") || "";
  const holdFilter = params.get("hold") || "all"; // all | only | exclude
  const overstayFilter = params.get("over") || "all"; // all | only | exclude
  const priorityFilter = params.get("prio") || "all"; // all | only
  const viewFilter = stage === "completed" ? "completed" : params.get("view") || "active"; // active | completed | completed_by_cleaners
  const searchFilter = params.get("q") || "";
  const assignmentFilter = params.get("assignment") || "all";
  const assignmentTarget = params.get("assignment_target") || "";
  const isRepairsStage = REPAIR_STAGES.has(stage);
  const configuredStage = effectiveWorkflow?.stages?.find(item => item.key === stage);
  const stageDisplayName = configuredStage?.display_name || STAGE_LABELS[stage] || "Workflow stage";
  const hasStageAccess = canAccessStage(user, stage);
  const canExecuteBulk=(user?.permission_keys||[]).includes("tar.bulk_actions.execute");

  const setFilter = (k, v) => {
    const next = Object.fromEntries(params.entries());
    if (v && v !== "all") next[k] = v;
    else delete next[k];
    setParams(next);
  };

  useEffect(() => {
    let active = true;
    api.get(`/me/preferences/stage-filters/${stage}`).then(({data}) => {
      if (!active) return;
      setSavedFilters(data.filters || null);
      if (data.filters && !window.location.search) setParams(data.filters, { replace: true });
    }).catch(() => { if (active) setSavedFilters(null); });
    return () => { active = false; };
  }, [stage, setParams]);

  const saveFilters = async () => {
    const filters = Object.fromEntries(params.entries());
    const {data} = await api.put(`/me/preferences/stage-filters/${stage}`, {filters});
    setSavedFilters(data.filters || {});
    toast.success(`Preferred filters saved for ${stageDisplayName}`);
  };
  const clearSavedFilters = async () => {
    await api.delete(`/me/preferences/stage-filters/${stage}`);
    setSavedFilters(null);
    toast.success(`Preferred filters cleared for ${stageDisplayName}`);
  };

  const load = useCallback(async () => {
    if (!hasStageAccess) {
      setRooms([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const [r, b, s] = await Promise.all([
        api.get(`/stages/${stage}/rooms`, { params: { view: viewFilter } }),
        api.get("/blocks"),
        api.get("/settings"),
      ]);
      setRooms(r.data);
      setBlocks(b.data);
      setSettings(s.data);
    } finally {
      setLoading(false);
    }
  }, [stage, viewFilter, hasStageAccess]);

  useEffect(() => {
    load();
  }, [load]);
  useEffect(()=>{if(!(user?.permission_keys||[]).includes("tar.bulk_actions.execute")){setConfiguredBulkActions([]);return;}api.get(`/stages/${stage}/bulk-actions`).then(({data})=>setConfiguredBulkActions(data)).catch(()=>setConfiguredBulkActions([]));},[stage,user?.permission_keys]);

  useEffect(() => {
    setPicked(new Set());
    setBulkMode(false);
    setBulkHoldReason("");
  }, [stage, viewFilter, typeFilter, blockFilter, holdFilter, priorityFilter]);

  useEffect(() => {
    let active = true;
    if (!hasStageAccess || !isRepairsStage) {
      setStageRepairs([]);
      return () => { active = false; };
    }
    const kind = stage === "internal_repairs" ? "internal" : "external";
    api.get("/repairs", { params: { kind } })
      .then(({ data }) => { if (active) setStageRepairs(data); })
      .catch(() => { if (active) setStageRepairs([]); });
    return () => { active = false; };
  }, [hasStageAccess, isRepairsStage, stage, viewFilter]);
  useEffect(()=>{if(!["person","role"].includes(assignmentFilter)||!rooms[0]){setAssignmentOptions({users:[],roles:[]});return;}api.get(`/rooms/${rooms[0].id}/stages/${stage}/assignment-options`).then(({data})=>setAssignmentOptions(data)).catch(()=>setAssignmentOptions({users:[],roles:[]}));},[assignmentFilter,rooms,stage]);

  useEffect(() => {
    if (!roomFromNotif || !rooms.length || selected || selectedStageForm) return;
    const target = rooms.find((r) => r.id === roomFromNotif);
    if (!target) return;
    const kind=stageModalKind(stage,configuredStage||{});
    if(kind==="key_return")setKeyReturnRoom(target);
    else if(["form","inspection"].includes(kind))setSelectedStageForm(target);
    else setSelected({ id: target.id, tab: STAGE_TAB[stage] || "workflow" });
  }, [roomFromNotif, rooms, selected, selectedStageForm, stage, configuredStage]);

  const filtered = useMemo(() => {
    return rooms.filter((r) => {
      if (typeFilter === "bedroom" && r.type !== "bedroom") return false;
      if (typeFilter === "common" && r.type !== "common") return false;
      if (blockFilter && r.block_id !== blockFilter) return false;
      const stageStatus = r.stages?.[stage]?.status;
      if (viewFilter === "active" && holdFilter === "only" && stageStatus !== "on_hold") return false;
      if (viewFilter === "active" && holdFilter === "exclude" && stageStatus === "on_hold") return false;
      if (overstayFilter === "only" && !r.overstayer) return false;
      if (overstayFilter === "exclude" && r.overstayer) return false;
      if (priorityFilter === "only" && !r.priority) return false;
      if (!assignmentMatches(r.stages?.[stage]?.assignment, assignmentFilter, user, assignmentTarget)) return false;
      if (searchFilter && !`${r.name||""} ${r.flat_name||""} ${r.block_name||""} ${r.site_name||""}`.toLowerCase().includes(searchFilter.toLowerCase())) return false;
      return true;
    });
  }, [rooms, typeFilter, blockFilter, holdFilter, overstayFilter, priorityFilter, stage, viewFilter, assignmentFilter, assignmentTarget, searchFilter, user]);


  const filteredRepairCount=isRepairsStage?filtered.reduce((count,room)=>count+repairRowsForRoom(room,stage,stageRepairs,viewFilter).length,0):null;
  const c = STAGE_COLORS[stage] || STAGE_COLORS.key_return;
  const showOverstayerFilter = stage === "key_return";
  const showHoldFilter = viewFilter === "active" && stage !== "key_return" && stage !== "completed";

  const togglePick = (id) => {
    setPicked((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const allFilteredIds = filtered.map((r) => r.id);
  const selectedRooms = filtered.filter(room=>picked.has(room.id));
  const selectedStatuses = selectedRooms.map(room=>room.stages?.[stage]?.status);
  const interactionMode=configuredStage?.interaction_mode||stageModalKind(stage,configuredStage||{});
  const allSelectedOnHold=selectedRooms.length>0&&selectedStatuses.every(status=>status==="on_hold");
  const allSelectedPending=selectedRooms.length>0&&selectedStatuses.every(status=>status==="pending");
  const allSelectedInProgress=selectedRooms.length>0&&selectedStatuses.every(status=>status==="in_progress");
  const allPicked = allFilteredIds.length > 0 && allFilteredIds.every((id) => picked.has(id));
  const togglePickAll = () => {
    if (allPicked) setPicked(new Set());
    else setPicked(new Set(allFilteredIds));
  };


  const refreshSelectedRepair = async (repairId) => {
    if (!repairId) return;
    const { data } = await api.get("/repairs");
    setSelectedRepair(data.find((r) => r.id === repairId) || null);
    load();
  };

  const repairAct = async (id, action, payload = {}) => {
    await api.patch(`/repairs/${id}`, { action, ...payload });
    toast.success("Repair updated");
    await refreshSelectedRepair(id);
  };

  const submitStageForm = async (room, payload) => {
    await api.post(`/rooms/${room.id}/stages/${stage}/action`, payload);
    toast.success("Stage updated");
    setSelectedStageForm(null);
    await load();
  };

  const raiseRepair = async (repair, payload) => {
    await api.post(`/repairs/${repair.id}/raise`, payload);
    toast.success(repair.status === "raised" ? "Raised repair updated" : "Repair raised");
    await refreshSelectedRepair(repair.id);
  };

  const completeContractorRepair = async (repair, payload) => {
    await api.post(`/repairs/${repair.id}/complete-cx`, payload);
    toast.success("Marked completed");
    await refreshSelectedRepair(repair.id);
  };

  const addRelatedRepair = async (repair, payload) => {
    await api.post(`/rooms/${repair.room_id}/repairs`, payload);
    toast.success("Repair added");
    await refreshSelectedRepair(repair.id);
  };

  const applyBulkStageAction = async (action, payload = {}) => {
    if (picked.size === 0) {
      toast.error("Pick at least one row");
      return;
    }
    if (action === "hold" && !bulkHoldReason.trim()) {
      toast.error("Hold reason required");
      return;
    }
    setBulkBusy(true);
    try {
      const body = action === "hold" ? { action, hold_reason: bulkHoldReason.trim() } : { action, ...payload };
      await Promise.all(Array.from(picked).map((roomId) => api.post(`/rooms/${roomId}/stages/${stage}/action`, body)));
      toast.success(`Updated ${picked.size} row${picked.size === 1 ? "" : "s"}`);
      setPicked(new Set());
      setBulkMode(false);
      setBulkHoldReason("");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Bulk stage update failed");
    } finally {
      setBulkBusy(false);
    }
  };

  const removeBulkAssignment=async()=>{if(!picked.size)return;setBulkBusy(true);try{await api.post(`/stages/${stage}/bulk-assignment`,{room_ids:[...picked],assignment_type:null,assignment_target_id:null,reason:"Bulk assignment removal"});toast.success("Assignments removed");setPicked(new Set());await load()}catch(e){toast.error(e.response?.data?.detail||"Assignments could not be removed")}finally{setBulkBusy(false)}};

  const applyBulkKeyStatus = async () => {
    if (picked.size === 0) {
      toast.error("Pick at least one room");
      return;
    }
    setBulkBusy(true);
    try {
      const { data } = await api.post("/rooms/bulk-flags", { room_ids: Array.from(picked), key_status: bulkKeyStatus });
      toast.success(`Updated ${data.updated} room${data.updated === 1 ? "" : "s"}`);
      setPicked(new Set());
      setBulkMode(false);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Bulk key status update failed");
    } finally {
      setBulkBusy(false);
    }
  };
  const applyConfiguredBulk=async action=>{if(!picked.size)return toast.error("Pick at least one row");if(action.confirmation_required&&!window.confirm(`${action.name}\n\n${action.description||"Apply this action to the selected Rooms?"}`))return;const reason=action.reason_required?window.prompt("Reason (required)"):null;if(action.reason_required&&!reason?.trim())return;setBulkBusy(true);try{const {data}=await api.post(`/stages/${stage}/bulk-actions/execute`,{bulk_action_id:action.id,room_ids:[...picked],reason,confirmed:true});setBulkResult(data);if(data.status==="success"){toast.success(`${data.succeeded_count} Room${data.succeeded_count===1?"":"s"} updated`);setPicked(new Set());await load()}else toast.error("Selection contains invalid stage states")}catch(e){toast.error(e.response?.data?.detail||"Bulk action failed")}finally{setBulkBusy(false)}};



  const blockOptions = useMemo(
    () => [{ id: "all", label: "All blocks" }, ...blocks.map((b) => ({ id: b.id, label: b.code }))],
    [blocks]
  );
  const exportCleaningCsv = () => {
    (async () => {
      const { data } = await api.get("/export/cleaning-list/file", {
        params: { fmt: cleaningExportFormat, block_id: blockFilter || undefined, view: viewFilter },
        responseType: "blob",
      });
      const url = URL.createObjectURL(data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `cleaning-list-${new Date().toISOString().slice(0, 10)}.${cleaningExportFormat}`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Cleaning list exported");
    })().catch((e) => toast.error(e.response?.data?.detail || "Export failed"));
  };

  const stageExportRows = useMemo(
    () => buildStageExportRows(stage, filtered, stageRepairs, viewFilter),
    [stage, filtered, stageRepairs, viewFilter]
  );

  const loadStageRepairs = async () => {
    if (!isRepairsStage) return stageRepairs;
    const kind = stage === "internal_repairs" ? "internal" : "external";
    const { data } = await api.get("/repairs", { params: { kind } });
    setStageRepairs(data);
    return data;
  };

  const exportStageCsv = async () => {
    try {
      const repairs = isRepairsStage ? await loadStageRepairs() : stageRepairs;
      downloadCsv(
        `stage-${stage}-${viewFilter}-${new Date().toISOString().slice(0, 10)}.csv`,
        buildStageExportRows(stage, filtered, repairs, viewFilter)
      );
      toast.success("Stage CSV exported");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Export failed");
    }
  };

  const printStage = async () => {
    try {
      if (isRepairsStage) {
        await loadStageRepairs();
        setTimeout(() => window.print(), 0);
      } else {
        window.print();
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Print data failed");
    }
  };

  if (!hasStageAccess) {
    return (
      <div className="bg-white border border-gray-200 p-8 text-center" data-testid="stage-access-denied">
        <h1 className="font-chivo font-black text-2xl mb-2">No access</h1>
        <p className="text-sm text-gray-600">You do not have access to the requested page.</p>
      </div>
    );
  }

  return (
    <div data-testid={`stage-page-${stage}`}>
      <PageHeader eyebrow="TARlution · Workflow" title={stageDisplayName} description={configuredStage?.description || TAB_DESC[stage] || "Rooms currently at this stage."} meta={isRepairsStage?`${filteredRepairCount} repair${filteredRepairCount===1?"":"s"} shown`:`${filtered.length} shown · ${rooms.length} total`}><div className="flex flex-wrap items-center justify-end gap-2">
          {stage !== "cleaning" && (
            <button onClick={exportStageCsv} data-testid="stage-export-csv" className="px-3 py-2 text-xs font-mono font-bold uppercase border border-gray-300 bg-white hover:border-black">
              Export CSV
            </button>
          )}
          <button onClick={printStage} data-testid="stage-print-pdf" className="px-3 py-2 text-xs font-mono font-bold uppercase border border-gray-300 bg-white hover:border-black">
            Print / PDF
          </button>
        </div></PageHeader>
      {stage === "cleaning" && (
        <div className="mb-4 flex items-center gap-2">
          <select
            value={cleaningExportFormat}
            onChange={(e) => setCleaningExportFormat(e.target.value)}
            className="border border-gray-300 px-2 py-2 text-xs font-mono bg-white"
          >
            <option value="csv">CSV</option>
            <option value="xlsx">XLSX</option>
          </select>
          <button onClick={exportCleaningCsv} data-testid="cleaning-export-csv" className="px-3 py-2 text-xs font-mono font-bold uppercase border border-black bg-black text-white">
            Export cleaning list
          </button>
        </div>
      )}

      <MobileFilterPanel className="bg-white border border-gray-200 p-3 mb-4 flex-wrap gap-3 items-center" testId="stage-filters">
        <label className="ops-field min-w-56"><span>Search</span><input type="search" value={searchFilter} onChange={e=>setFilter("q",e.target.value)} placeholder="Room, Flat, Block or Site"/></label>
        {stage !== "completed" && (
          <FilterGroup label="View" testId="filter-view" value={viewFilter} onChange={(v) => setFilter("view", v)} options={stage === "bag_and_bin" ? BAG_AND_BIN_VIEW_OPTIONS : stage === "cleaning" ? [...VIEW_OPTIONS,{id:"awaiting_signoff",label:"Awaiting sign-off"}] : VIEW_OPTIONS} />
        )}
        <FilterGroup label="Type" testId="filter-type" value={typeFilter} onChange={(v) => setFilter("type", v)} options={TYPE_OPTIONS} />
        <FilterGroup label="Block" testId="filter-block" value={blockFilter || "all"} onChange={(v) => setFilter("block", v === "all" ? "" : v)} options={blockOptions} />
        {showOverstayerFilter && (
          <FilterGroup label="Overstayers" testId="filter-overstay" value={overstayFilter} onChange={(v) => setFilter("over", v)} options={TRI_OPTIONS} />
        )}
        {showHoldFilter && (
          <FilterGroup label="On hold" testId="filter-hold" value={holdFilter} onChange={(v) => setFilter("hold", v)} options={TRI_OPTIONS} />
        )}
        <FilterGroup label="Priority" testId="filter-priority" value={priorityFilter} onChange={(v) => setFilter("prio", v)} options={BI_OPTIONS} />
        <FilterSelect label="Assignment" testId="filter-assignment" value={assignmentFilter} onChange={(v)=>setFilter("assignment",v)} options={ASSIGNMENT_FILTER_OPTIONS} />
        {["person","role"].includes(assignmentFilter)&&<FilterSelect label={assignmentFilter==="person"?"Person":"Role / team"} testId="filter-assignment-target" value={assignmentTarget} onChange={value=>setFilter("assignment_target",value)} options={[{id:"",label:assignmentFilter==="person"?"Choose a person":"Choose a role/team"},...(assignmentFilter==="person"?assignmentOptions.users:assignmentOptions.roles)]}/>}
        {canExecuteBulk && stage !== "completed" && viewFilter === "active" && (
          <div className="ml-auto">
            <div className="text-[10px] font-mono uppercase tracking-wider text-gray-500 mb-1">Bulk</div>
            <button
              onClick={() => { setBulkMode((v) => !v); setPicked(new Set()); setBulkHoldReason(""); }}
              data-testid="bulk-toggle"
              className={`px-2 py-1 text-[10px] font-mono font-bold uppercase border transition-colors ${
                bulkMode ? "bg-black text-white border-black" : "bg-white border-gray-300 hover:border-black"
              }`}
            >
              {bulkMode ? "Cancel bulk" : "Bulk select"}
            </button>
          </div>
        )}
        <div className="flex gap-2">
          <button type="button" onClick={saveFilters} className="min-h-11 rounded-lg border bg-white px-3 text-xs font-semibold">Save current filters</button>
          {savedFilters && <button type="button" onClick={clearSavedFilters} className="min-h-11 rounded-lg border bg-white px-3 text-xs font-semibold">Clear saved filters</button>}
        </div>
      </MobileFilterPanel>

      {bulkMode && canExecuteBulk && stage !== "completed" && viewFilter === "active" && (
        <div className="bg-yellow-50 border border-yellow-300 p-3 mb-4 flex flex-wrap items-end gap-3" data-testid="bulk-toolbar">
          <div className="text-xs font-mono font-bold uppercase tracking-wider text-yellow-900 pb-2">
            {picked.size} selected
          </div>
          <button
            onClick={togglePickAll}
            data-testid="bulk-pick-all"
            className="px-2 py-1 text-[10px] font-mono font-bold uppercase border border-gray-400 bg-white hover:border-black mb-1"
          >
            {allPicked ? "Unpick all" : "Pick all filtered"}
          </button>
          {(user?.permission_keys||[]).includes("tar.stages.assign")&&picked.size>0&&<button type="button" onClick={()=>setBulkAssignOpen(value=>!value)} className="min-h-11 rounded border border-indigo-700 bg-white px-3 text-sm font-semibold text-indigo-700">Assign selected</button>}
          {(user?.permission_keys||[]).includes("tar.stages.assign")&&selectedRooms.some(room=>room.stages?.[stage]?.assignment)&&<button type="button" disabled={bulkBusy} onClick={removeBulkAssignment} className="min-h-11 rounded-lg border bg-white px-3 text-sm font-semibold">Remove assignment</button>}
          {allSelectedOnHold&&<button type="button" disabled={bulkBusy} onClick={()=>applyBulkStageAction("resume")} className="min-h-11 rounded-lg border bg-white px-3 text-sm font-semibold">Resume selected</button>}
          {allSelectedPending&&interactionMode==="work"&&!isRepairsStage&&<button type="button" disabled={bulkBusy} onClick={()=>applyBulkStageAction("start")} className="min-h-11 rounded-lg border bg-white px-3 text-sm font-semibold">Start selected</button>}
          {allSelectedInProgress&&interactionMode==="work"&&!isRepairsStage&&<button type="button" disabled={bulkBusy} onClick={()=>applyBulkStageAction("complete")} className="min-h-11 rounded-lg bg-slate-950 px-3 text-sm font-semibold text-white">Complete selected</button>}
          <label className="text-[10px] font-mono uppercase tracking-wider text-gray-600">
            Hold reason
            <input
              value={bulkHoldReason}
              onChange={(e) => setBulkHoldReason(e.target.value)}
              disabled={bulkBusy || picked.size === 0}
              data-testid="bulk-hold-reason"
              className="mt-1 block w-56 border border-gray-400 bg-white px-2 py-1 text-xs normal-case disabled:opacity-40"
              placeholder="Required to place on hold"
            />
          </label>
          <button
            disabled={bulkBusy || picked.size === 0}
            onClick={() => applyBulkStageAction("hold")}
            data-testid="bulk-stage-hold"
            className="px-3 py-1 text-[10px] font-mono font-bold uppercase border border-amber-700 text-amber-800 bg-white hover:bg-amber-50 disabled:opacity-40 mb-1"
          >
            Place on hold
          </button>
          {stage === "bag_and_bin" && canReturnBagBin && (
            <button
              disabled={bulkBusy || picked.size === 0}
              onClick={() => applyBulkStageAction("defer_to_cleaner")}
              data-testid="bulk-bagbin-defer"
              className="px-3 py-1 text-[10px] font-mono font-bold uppercase border border-blue-700 text-blue-700 bg-white hover:bg-blue-50 disabled:opacity-40 mb-1"
            >
              Delegate to Cleaner
            </button>
          )}
          {stage === "cleaning" && (
            <button
              disabled={bulkBusy || picked.size === 0}
              onClick={() => applyBulkStageAction("complete", { note: "Bulk completed from Cleaning stage" })}
              data-testid="bulk-cleaning-complete"
              className="px-3 py-1 text-[10px] font-mono font-bold uppercase border border-black bg-black text-white disabled:opacity-40 mb-1"
            >
              Complete cleaning
            </button>
          )}
          {stage === "key_return" && (
            <div className="flex items-end gap-2 flex-wrap">
              <label className="text-[10px] font-mono uppercase tracking-wider text-gray-600">
                Key status
                <select
                  value={bulkKeyStatus}
                  onChange={(e) => setBulkKeyStatus(e.target.value)}
                  disabled={bulkBusy || picked.size === 0}
                  data-testid="bulk-key-status-select"
                  className="mt-1 block border border-gray-400 bg-white px-2 py-1 text-[10px] font-mono uppercase disabled:opacity-40"
                >
                  {KEY_STATUSES.map((status) => (
                    <option key={status} value={status}>{status}</option>
                  ))}
                </select>
              </label>
              <button
                disabled={bulkBusy || picked.size === 0}
                onClick={applyBulkKeyStatus}
                data-testid="bulk-key-status-apply"
                className="px-3 py-1 text-[10px] font-mono font-bold uppercase border border-black bg-black text-white disabled:opacity-40 mb-1"
              >
                Update key status
              </button>
            </div>
          )}
        </div>
      )}
      {bulkAssignOpen&&picked.size>0&&<div className="mb-4"><AssignmentPicker optionsUrl={`/rooms/${[...picked][0]}/stages/${stage}/assignment-options`} saveUrl={`/stages/${stage}/bulk-assignment`} roomIds={[...picked]} onSaved={()=>{setBulkAssignOpen(false);setPicked(new Set());load()}}/></div>}
      {bulkMode&&picked.size>0&&configuredBulkActions.length>0&&<div className="sticky top-16 z-10 mb-4 flex flex-wrap items-center gap-2 rounded-xl border border-indigo-200 bg-indigo-50 p-3 shadow" data-testid="configured-bulk-toolbar"><strong className="mr-auto text-sm">{picked.size} selected</strong>{configuredBulkActions.map(action=><button key={action.id} disabled={bulkBusy} onClick={()=>applyConfiguredBulk(action)} className="min-h-11 rounded-lg bg-indigo-700 px-4 text-sm font-semibold text-white disabled:opacity-50">{action.name}</button>)}<button onClick={()=>setPicked(new Set())} className="min-h-11 rounded-lg border bg-white px-4">Clear</button></div>}
      {bulkResult?.errors?.length>0&&<div role="alert" className="mb-4 rounded-lg border border-red-200 bg-red-50 p-3"><strong>Some selected Rooms could not be updated.</strong><ul className="mt-2 text-sm">{bulkResult.errors.map(item=><li key={item.room_id}>{item.error}</li>)}</ul></div>}

      {loading ? (
        <div className="text-sm text-gray-500">Loading…</div>
      ) : filtered.length === 0 ? (
        <EmptyState title="No work matches these filters" description="Adjust the filters or choose another Site to find operational work." />
      ) : isRepairsStage ? (
        <RepairStageList rooms={filtered} repairs={stageRepairs} stage={stage} viewFilter={viewFilter} onOpen={setSelectedRepair} settings={settings}/>
      ) : (
        <div className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
          <div className="hidden grid-cols-[36px_minmax(220px,2fr)_minmax(160px,1.1fr)_minmax(110px,.8fr)_minmax(140px,1fr)_minmax(120px,.8fr)] gap-4 border-b bg-slate-50 px-4 py-3 text-[10px] font-bold uppercase tracking-wider text-slate-500 md:grid">
            <div>{bulkMode ? <input type="checkbox" checked={allPicked} onChange={togglePickAll} data-testid="bulk-header-checkbox" /> : null}</div>
            <div>Room and location</div>
            <div>{stageMetadata(stage).map(column=>column.label).join(" · ")}</div>
            <div>Flags</div>
            <div>Assignment</div>
            <div>Status</div>
          </div>
          {filtered.map((r) => (
            <Row
              key={r.id}
              r={r}
              stage={stage}
              settings={settings}
              bulkMode={bulkMode}
              picked={picked.has(r.id)}
              onTogglePick={togglePick}
              onClick={() => { const kind=stageModalKind(stage,configuredStage||{}); if(kind==="key_return")setKeyReturnRoom(r); else if(["form","inspection"].includes(kind))setSelectedStageForm(r); else setSelected({ id:r.id,tab:STAGE_TAB[stage]||"workflow" }); }}
            />
          ))}
        </div>
      )}

      {selectedStageForm && ["checkout_inspection", "maintenance_inspection"].includes(stage) && (
        <InspectionFormDialog
          stage={stage}
          displayName={stageDisplayName}
          room={selectedStageForm}
          onClose={() => setSelectedStageForm(null)}
          onDone={() => {
            setSelectedStageForm(null);
            load();
          }}
        />
      )}

      {selectedStageForm && !["checkout_inspection", "maintenance_inspection"].includes(stage) && (
        <StageFormDialog
          stage={stage}
          displayName={stageDisplayName}
          questions={configuredStage?.questions||[]}
          room={selectedStageForm}
          onClose={() => setSelectedStageForm(null)}
          onSubmit={(payload) => submitStageForm(selectedStageForm, payload)}
        />
      )}

      {keyReturnRoom && <KeyReturnDialog room={keyReturnRoom} displayName={stageDisplayName} onClose={()=>setKeyReturnRoom(null)} onDone={()=>{setKeyReturnRoom(null);load();}} />}

      {selectedRepair && (
        <RepairForm
          repair={selectedRepair}
          onClose={() => setSelectedRepair(null)}
          onAct={repairAct}
          onRaise={raiseRepair}
          onCompleteContractor={completeContractorRepair}
          onAddRepair={addRelatedRepair}
        />
      )}

      <StagePrintSection
        title={stageDisplayName}
        description={TAB_DESC[stage] || "Rooms currently at this stage."}
        rows={stageExportRows}
        filters={{ view: viewFilter, type: typeFilter, block: blockFilter || "All", hold: holdFilter, priority: priorityFilter }}
      />

      {selected && (
        <RoomDrawer
          roomId={selected.id}
          initialTab={selected.tab}
          focusStage={stage}
          lockedStage={stage}
          closeOnStageAction
          onClose={() => {
            setSelected(null);
            if (roomFromNotif) {
              const next = Object.fromEntries(params.entries());
              delete next.room;
              setParams(next);
            }
            load();
          }}
        />
      )}
    </div>
  );
}

function RepairStageList({rooms,repairs,stage,viewFilter,onOpen,settings}) {
  const roomById=new Map(rooms.map(room=>[room.id,room]));
  const kind=stage==="internal_repairs"?"internal":"external";
  const completedView=viewFilter==="completed"||viewFilter==="completed_by_cleaners";
  const rows=(repairs||[]).filter(repair=>repair.kind===kind&&roomById.has(repair.room_id)&&(completedView?repair.status==="completed":repair.status!=="completed"));
  if(!rows.length)return <EmptyState title="No repairs match these filters" description="Counts and rows use the same filtered repair records."/>;
  return <div className="overflow-hidden rounded-xl border bg-white"><div className="hidden grid-cols-[minmax(220px,1.5fr)_minmax(180px,1fr)_minmax(160px,1fr)_120px_minmax(150px,1fr)] gap-4 border-b bg-slate-50 px-4 py-3 text-xs font-bold uppercase text-slate-500 md:grid"><span>Repair</span><span>Location</span><span>Room details</span><span>Status</span><span>{kind==="internal"?"Assignment":"Contractor"}</span></div>{rows.map(repair=>{const room=roomById.get(repair.room_id),location=repairLocation({...room,room_name:room.name});return <button key={repair.id} type="button" onClick={()=>onOpen(repair)} className="grid w-full gap-3 border-b p-4 text-left last:border-0 hover:bg-slate-50 md:grid-cols-[minmax(220px,1.5fr)_minmax(180px,1fr)_minmax(160px,1fr)_120px_minmax(150px,1fr)] md:items-center" style={repair.priority?{backgroundColor:settings.highlight_priority}:undefined}><div><strong>{repair.description||"Repair description unavailable"}</strong><span className="mt-1 block text-xs font-semibold text-amber-800">{repair.priority?"High priority":"Standard priority"}</span></div><div><strong className="block">{location.primary}</strong><span className="text-xs text-slate-500">{location.secondary}</span></div><dl className="text-xs"><dt className="text-slate-500">Room Type</dt><dd className="font-semibold">{room.room_type_name||room.type||"Not recorded"}</dd><dt className="mt-1 text-slate-500">Bed Size</dt><dd className="font-semibold">{room.bed_size||"Not applicable"}</dd></dl><StageStatusPill status={repair.status}/><div>{kind==="internal"?<AssignmentBadge assignment={repair.assignment}/>:<><strong className="block text-xs">{repair.contractor_name||"Contractor not assigned"}</strong>{repair.job_number&&<span className="text-xs text-slate-500">Job {repair.job_number}</span>}</>}</div></button>})}</div>;
}

function StagePrintSection({ title, description, rows, filters }) {
  const [header = [], ...bodyRows] = rows || [];
  return (
    <div className="stage-print print-only">
      <div className="stage-print-header">
        <h1>{title}</h1>
        <p>{description}</p>
        <div className="stage-print-meta">
          <span>Printed: {new Date().toLocaleString()}</span>
          <span>View: {filters.view}</span>
          <span>Type: {filters.type}</span>
          <span>Block: {filters.block}</span>
          <span>Hold: {filters.hold}</span>
          <span>Priority: {filters.priority}</span>
        </div>
      </div>
      <table className="stage-print-table">
        <thead>
          <tr>{header.map((h) => <th key={h}>{h}</th>)}</tr>
        </thead>
        <tbody>
          {bodyRows.map((row, idx) => (
            <tr key={`print-row-${idx}`}>
              {row.map((cell, cellIdx) => <td key={`print-cell-${idx}-${cellIdx}`}>{cell || "—"}</td>)}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function pickRowBg(picked, priority, stale, summerStayWelcome, settings) {
  if (picked) return "#FEF3C7";
  if (summerStayWelcome) return "#E0F2FE";
  if (priority) return settings.highlight_priority;
  if (stale) return settings.highlight_stale;
  return "white";
}

export function Row({ r, stage, settings, onClick, bulkMode, picked, onTogglePick }) {
  const stageStatus = stage === "completed" ? "completed" : r.stages?.[stage]?.status;
  const stageNote = stage === "completed" ? "" : r.stages?.[stage]?.note;
  const isStale = stage !== "completed" && r.stale;
  const isSummerStayWelcome = stage === "welcome_packs" && r.flags?.summer_stay;
  const bg = pickRowBg(picked, r.priority, isStale, isSummerStayWelcome, settings);
  const handleClick = bulkMode ? () => onTogglePick(r.id) : onClick;
  const location=repairLocation({...r,room_name:r.name});
  const metadata=stageMetadata(stage);
  return (
    <button
      onClick={handleClick}
      data-testid={`room-row-${r.id}`}
      className="operational-row grid w-full grid-cols-[28px_minmax(0,1fr)] gap-3 border-b border-slate-100 px-4 py-4 text-left last:border-b-0 hover:bg-slate-50 md:grid-cols-[36px_minmax(220px,2fr)_minmax(160px,1.1fr)_minmax(110px,.8fr)_minmax(140px,1fr)_minmax(120px,.8fr)] md:items-center md:gap-4"
      style={{ backgroundColor: bg }}
    >
      <div className="flex flex-col gap-0.5">
        {bulkMode ? (
          <input
            type="checkbox"
            checked={picked}
            onChange={(e) => { e.stopPropagation(); onTogglePick(r.id); }}
            onClick={(e) => e.stopPropagation()}
            data-testid={`bulk-row-${r.id}`}
          />
        ) : (
          <>
            {r.priority && <Star size={14} fill="#CA8A04" stroke="#CA8A04" data-testid={`row-priority-${r.id}`} />}
            {isStale && <Clock size={14} stroke="#C2410C" data-testid={`row-stale-${r.id}`} />}
            {r.overstayer && <AlertTriangle size={14} stroke="#B91C1C" />}
            {isSummerStayWelcome && <AlertTriangle size={14} stroke="#0284C7" data-testid={`row-summer-stay-${r.id}`} />}
          </>
        )}
      </div>
      <div className="min-w-0">
        <div className="font-chivo font-bold text-sm break-words whitespace-normal flex items-center gap-2">
          {location.primary}
          {r.type === "common" && (
            <span className="text-[9px] font-mono font-bold text-gray-500 uppercase border border-gray-300 px-1">Common</span>
          )}
          {r.type === "common" && r.flat_has_summer_stay && (
            <span className="text-[9px] font-mono font-bold text-sky-800 uppercase border border-sky-300 bg-sky-50 px-1" data-testid={`row-common-summer-stay-${r.id}`}>Summer stay in flat</span>
          )}
        </div>
        <div className="text-[10px] font-mono text-gray-500 uppercase mt-0.5 break-words whitespace-normal">
          {location.secondary}
          {isStale && (
            <span className="ml-2 text-orange-700">
              · stale {r.stale_hours ? `${r.stale_hours}h` : ""}
            </span>
          )}
        </div>
      </div>
      <div className="col-start-2 text-xs text-slate-700 md:col-auto">
        <dl className="flex flex-wrap gap-x-4 gap-y-2">{metadata.map(column=>{const value=column.value(r);return value?<div key={column.key}><dt className="text-[9px] font-bold uppercase tracking-wide text-slate-400">{column.label}</dt><dd className="font-semibold">{value}</dd></div>:null})}</dl>
        {stage === "cleaning" && r.stages?.cleaning?.note?.toLowerCase().includes("reopened") && (
          <div className="mt-1 text-[10px] font-mono border border-red-300 bg-red-50 text-red-700 px-1.5 py-0.5 inline-block">
            Returned: {r.stages?.cleaning_inspection?.form_data?.fail_reason || "No reason entered"}
          </div>
        )}
        {isSummerStayWelcome && (
          <div className="mt-1 text-[10px] font-mono font-bold border border-sky-300 bg-sky-50 text-sky-800 px-1.5 py-0.5 inline-block" data-testid={`welcome-summer-stay-warning-${r.id}`}>
            Summer stay: knock before entering
          </div>
        )}
        {r.type === "common" && r.flat_has_summer_stay && (
          <div className="mt-1 text-[10px] font-mono font-bold border border-sky-300 bg-sky-50 text-sky-800 px-1.5 py-0.5 inline-block" data-testid={`common-flat-summer-stay-warning-${r.id}`}>
            Summer stayer in this flat
          </div>
        )}
      </div>
      <div className="col-start-2 flex flex-wrap gap-1 md:col-auto">
        {r.flags?.rebooking && <FlagPip kind="rebooking" />}
        {r.flags?.storage && <FlagPip kind="storage" />}
        {r.flags?.summer_stay && <FlagPip kind="summer_stay" />}
        {r.flags?.refurbishment && <FlagPip kind="refurbishment" />}
        {(r.flags?.occupied_override ?? (r.key_status === "Room Occupied")) && <FlagPip kind="occupied" />}
      </div>
      <div className="col-start-2 md:col-auto"><AssignmentBadge assignment={r.stages?.[stage]?.assignment}/></div>
      <div className="col-start-2 md:col-auto">
        <StageStatusPill status={stageStatus || "pending"} />
        {stageStatus === "on_hold" && stageNote && (
          <div className="mt-1 text-[10px] font-mono border border-amber-300 bg-amber-50 text-amber-800 px-1.5 py-0.5" data-testid={`row-hold-reason-${r.id}`}>
            Hold: {stageNote}
          </div>
        )}
      </div>
    </button>
  );
}

function StageStatusPill({ status }) {
  const map = {
    pending: ["#e5e7eb", "#374151"],
    in_progress: ["#DBEAFE", "#1D4ED8"],
    on_hold: ["#FEF3C7", "#B45309"],
    completed: ["#DCFCE7", "#15803D"],
    skipped: ["#F3F4F6", "#6B7280"],
  };
  const [bg, fg] = map[status] || map.pending;
  return (
    <span className="text-[10px] font-mono font-bold uppercase tracking-wider px-1.5 py-0.5 border" style={{ backgroundColor: bg, color: fg, borderColor: fg }}>
      {friendlyStatus(status)}
    </span>
  );
}

function FilterGroup({ label, value, onChange, options, testId }) {
  return (
    <div data-testid={testId}>
      <div className="text-[10px] font-mono uppercase tracking-wider text-gray-500 mb-1">{label}</div>
      <div className="flex flex-wrap gap-1">
        {options.map((o) => (
          <button
            key={o.id}
            data-testid={`${testId}-${o.id}`}
            onClick={() => onChange(o.id)}
            className={`px-2 py-1 text-[10px] font-mono font-bold uppercase border transition-colors ${
              value === o.id ? "bg-black text-white border-black" : "bg-white border-gray-300 hover:border-black"
            }`}
          >
            {o.label}
          </button>
        ))}
      </div>
    </div>
  );
}

function FilterSelect({label,value,onChange,options,testId}) { return <label className="ops-field min-w-48" data-testid={testId}><span>{label}</span><select value={value} onChange={event=>onChange(event.target.value)}>{options.map(option=><option key={option.id} value={option.id}>{option.label||option.name}</option>)}</select></label>; }

function KeyReturnDialog({room,displayName,onClose,onDone}) {
  const location=repairLocation({...room,room_name:room.name});
  const [options,setOptions]=useState([]),[outcome,setOutcome]=useState(""),[busy,setBusy]=useState(false);
  useEffect(()=>{api.get(`/rooms/${room.id}/operational-options`).then(({data})=>setOptions(data.key_return_options||[])).catch(()=>toast.error("Could not load Key Return outcomes"));},[room.id]);
  const confirm=async event=>{event.preventDefault();if(!outcome){toast.error("Choose a key-return outcome");return;}setBusy(true);try{await api.post(`/rooms/${room.id}/key-return-outcome`,{outcome_id:outcome});toast.success(`${displayName} completed`);onDone();}catch(error){toast.error(error.response?.data?.detail||"Key Return could not be completed");}finally{setBusy(false);}};
  return <ResponsiveDialog title={location.primary} subtitle={location.secondary} onClose={onClose} busy={busy} className="sm:max-w-lg" footer={<button type="submit" form="key-return-form" disabled={busy||!outcome} className="min-h-11 w-full rounded-lg bg-slate-950 px-4 font-semibold text-white disabled:opacity-50">{busy?"Submitting…":"Confirm Key Return"}</button>}><form id="key-return-form" onSubmit={confirm} className="space-y-4 p-5" data-testid="key-return-direct-form"><div><p className="text-xs font-bold uppercase tracking-wide text-slate-500">{displayName}</p><h3 className="mt-1 text-lg font-bold">Record the returned keys</h3><p className="mt-1 text-sm text-slate-600">Choose the outcome and confirm once. The workflow will route the Room automatically.</p></div><label className="ops-field"><span>Keys Returned outcome</span><select aria-label="Keys Returned outcome" value={outcome} onChange={event=>setOutcome(event.target.value)} required><option value="">Choose an outcome…</option>{options.map(option=><option key={option.id} value={option.id}>{option.name||option.label}</option>)}</select></label></form></ResponsiveDialog>;
}
